import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost } from './api';
import { clearProjectsSessionCaches } from './projectsSessionCache';
import {
	clearSkipVerificationBanner,
	clearVerificationBannerDismissed,
	markSkipVerificationBannerForSession,
} from '@utils/emailVerificationBanner';

export type UserRole = 'admin' | 'company' | 'gardener' | 'seeker';
export type AdminScope = 'full' | 'insights';
export type GardenerType = 'freelance' | 'employed';
export type AdminCapability =
	| 'admin_analytics'
	| 'admin_engagement'
	| 'admin_user_activity'
	| 'admin_moderation'
	| 'admin_issues'
	| 'admin_errors'
	| 'admin_view_as';
export type AppCapability =
	| 'dashboard'
	| 'marketplace'
	| 'direct_plans'
	| 'projects'
	| 'schedule'
	| 'tasks'
	| 'team'
	| 'my_plans'
	| 'my_projects'
	| 'create_plan';

export type AuthenticatedUser = {
	id: string;
	name: string;
	email: string;
	role: UserRole;
	gardenerType?: GardenerType;
	companyName?: string;
	companyId?: number;
	activeCompanyId?: number | null;
	companyIds?: number[];
	adminScope?: AdminScope;
	organizationId?: string;
	emailVerified?: boolean;
	emailVerifiedAt?: string | null;
	verificationGraceExpiresAt?: string | null;
	isRestricted?: boolean;
};

type LoginPayload = {
	email: string;
	password: string;
};

type RegisterPayload = {
	email: string;
	password: string;
	name: string;
	role: UserRole;
	gardenerType?: GardenerType;
	companyName?: string;
	companyId?: number;
	requestToJoinCompanyId?: number;
	gardenerInviteCode?: string;
};

type AuthContextValue = {
	user: AuthenticatedUser | null;
	login: (payload: LoginPayload) => Promise<void>;
	loginWithGoogle: (idToken: string) => Promise<void>;
	register: (payload: RegisterPayload) => Promise<void>;
	refreshUser: (userId: string) => Promise<void>;
	logout: () => void;
	isReady: boolean;
	isLoading: boolean;
	error: string | null;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const STORAGE_KEY = 'gardenhub:auth';

interface LoginResponse {
	userId: number;
	user: {
		id: number;
		email: string;
		name: string;
		role: UserRole;
		gardener_type?: GardenerType;
		company_name?: string;
		company_id?: number;
		active_company_id?: number | null;
		company_ids?: number[];
		admin_scope?: AdminScope | null;
		email_verified?: boolean;
		email_verified_at?: string | null;
		verification_grace_expires_at?: string | null;
		is_restricted?: boolean;
	};
}

interface RegisterResponse {
	userId: number;
	user: {
		id: number;
		email: string;
		name: string;
		role: UserRole;
		gardenerType?: GardenerType;
		companyName?: string;
		admin_scope?: AdminScope | null;
		email_verified?: boolean;
		email_verified_at?: string | null;
		verification_grace_expires_at?: string | null;
		is_restricted?: boolean;
	};
}

function convertUserFromApi(apiUser: LoginResponse['user']): AuthenticatedUser {
	return {
		id: String(apiUser.id),
		name: apiUser.name,
		email: apiUser.email,
		role: apiUser.role,
		gardenerType: apiUser.gardener_type,
		companyName: apiUser.company_name || undefined,
		companyId: apiUser.company_id,
		activeCompanyId: apiUser.active_company_id ?? apiUser.company_id ?? null,
		companyIds: Array.isArray(apiUser.company_ids) ? apiUser.company_ids : (apiUser.company_id ? [apiUser.company_id] : []),
		adminScope: apiUser.role === 'admin' ? (apiUser.admin_scope ?? 'full') : undefined,
		emailVerified: apiUser.email_verified ?? false,
		emailVerifiedAt: apiUser.email_verified_at ?? null,
		verificationGraceExpiresAt: apiUser.verification_grace_expires_at ?? null,
		isRestricted: apiUser.is_restricted ?? false,
	};
}

export function getAdminScope(user: Pick<AuthenticatedUser, 'role' | 'adminScope'> | null | undefined): AdminScope {
	if (!user || user.role !== 'admin') {
		return 'full';
	}
	return user.adminScope ?? 'full';
}

export function isLimitedAdmin(user: Pick<AuthenticatedUser, 'role' | 'adminScope'> | null | undefined): boolean {
	return !!user && user.role === 'admin' && getAdminScope(user) === 'insights';
}

export function hasAdminCapability(
	user: Pick<AuthenticatedUser, 'role' | 'adminScope'> | null | undefined,
	capability: AdminCapability
): boolean {
	if (!user || user.role !== 'admin') {
		return false;
	}
	const scope = getAdminScope(user);
	if (scope === 'full') {
		return true;
	}
	return capability === 'admin_analytics';
}

export function hasAppCapability(
	user: Pick<AuthenticatedUser, 'role' | 'adminScope'> | null | undefined,
	capability: AppCapability
): boolean {
	if (!user) {
		return false;
	}
	if (user.role !== 'admin') {
		return true;
	}
	if (!isLimitedAdmin(user)) {
		return true;
	}
	return capability === 'dashboard';
}

export function AuthProvider({ children }: { children: ReactNode }) {
	const [user, setUser] = useState<AuthenticatedUser | null>(() => {
		if (typeof window === 'undefined') {
			return null;
		}
		try {
			const stored = window.sessionStorage.getItem(STORAGE_KEY);
			if (!stored) {
				return null;
			}
			return JSON.parse(stored) as AuthenticatedUser;
		} catch (error) {
			console.warn('Unable to restore session', error);
			return null;
		}
	});
	const [isReady, setIsReady] = useState(typeof window === 'undefined' ? false : true);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (typeof window === 'undefined') {
			return;
		}
		if (!user) {
			window.sessionStorage.removeItem(STORAGE_KEY);
		} else {
			window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(user));
		}
	}, [user]);

	useEffect(() => {
		if (typeof window === 'undefined') {
			setIsReady(false);
			return;
		}
		setIsReady(true);
	}, []);

	const login = useCallback(async (payload: LoginPayload) => {
		setIsLoading(true);
		setError(null);
		try {
			const response = await apiPost<LoginResponse>('/login', {
				email: payload.email,
				password: payload.password,
			});

			const authenticatedUser = convertUserFromApi(response.user);
			clearSkipVerificationBanner();
			setUser(authenticatedUser);
			// Persist immediately so callers can rely on STORAGE_KEY right after login()
			if (typeof window !== 'undefined') {
				try {
					window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(authenticatedUser));
				} catch {}
			}
		} catch (err: any) {
			const errorMessage = err.message || 'Login failed. Please check your credentials.';
			setError(errorMessage);
			throw err; // Re-throw so component can handle it
		} finally {
			setIsLoading(false);
		}
	}, []);

	const loginWithGoogle = useCallback(async (idToken: string) => {
		setIsLoading(true);
		setError(null);
		try {
			const response = await apiPost<LoginResponse>('/google-auth', {
				idToken: idToken,
			});

			const authenticatedUser = convertUserFromApi(response.user);
			clearSkipVerificationBanner();
			setUser(authenticatedUser);
			if (typeof window !== 'undefined') {
				try {
					window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(authenticatedUser));
				} catch {}
			}
		} catch (err: any) {
			const errorMessage = err.message || 'Google login failed. Please try again.';
			setError(errorMessage);
			throw err; // Re-throw so component can handle it
		} finally {
			setIsLoading(false);
		}
	}, []);

	const register = useCallback(async (payload: RegisterPayload) => {
		setIsLoading(true);
		setError(null);
		try {
			const response = await apiPost<RegisterResponse>('/register', {
				email: payload.email,
				password: payload.password,
				name: payload.name,
				role: payload.role,
				gardenerType: payload.gardenerType,
				companyName: payload.companyName,
				companyId: payload.companyId,
				requestToJoinCompanyId: payload.requestToJoinCompanyId,
				gardenerInviteCode: payload.gardenerInviteCode,
			});

			const authenticatedUser = convertUserFromApi(response.user as any);
			markSkipVerificationBannerForSession(authenticatedUser.id);
			setUser(authenticatedUser);
			if (typeof window !== 'undefined') {
				try {
					window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(authenticatedUser));
				} catch {}
			}
		} catch (err: any) {
			const errorMessage = err.message || 'Registration failed. Please try again.';
			setError(errorMessage);
			throw err; // Re-throw so component can handle it
		} finally {
			setIsLoading(false);
		}
	}, []);

	const refreshUser = useCallback(async (userId: string) => {
		try {
			const data = await apiGet<{ user: LoginResponse['user'] }>(`/users/me?userId=${encodeURIComponent(userId)}`);
			if (data?.user) {
				const nextUser = convertUserFromApi(data.user);
				if (nextUser.emailVerified) {
					clearVerificationBannerDismissed(nextUser.id);
				}
				setUser(nextUser);
			}
		} catch (_e) {}
	}, []);

	const logout = useCallback(() => {
		clearProjectsSessionCaches();
		clearSkipVerificationBanner();
		setUser(null);
		setError(null);
	}, []);

	const value = useMemo<AuthContextValue>(
		() => ({
			user,
			login,
			loginWithGoogle,
			register,
			refreshUser,
			logout,
			isReady,
			isLoading,
			error
		}),
		[user, login, loginWithGoogle, register, refreshUser, logout, isReady, isLoading, error]
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider');
	}
	return context;
}
